// frontend/src/shared/lib/calendarEvents.ts
//
// Связь «уведомления → календарь»: какие event_type'ы меняют содержимое
// календаря пользователя (вкладка «Календарь» в доке App.vue агрегирует
// mentor_events + registered_events + pending_holds из /calendars/schedule/).
//
// Это BOOKING_* (консультации/сессии) и EVENT_* (сессии). На оплате рядом
// с PAYMENT_SUCCEEDED прилетает BOOKING_CREATED, поэтому отдельно PAYMENT_ не
// нужен. Префиксы — зеркало backend/PlatformaAPP/notifications/categories.py.

import { sessionEndGraceMinutes } from '@/shared/conferenceConfig';
import { sessionGraceMin } from '@/shared/platformFlags';

const CALENDAR_PREFIXES = ['BOOKING_', 'EVENT_'] as const;

/** true, если уведомление с таким event_type меняет календарь пользователя. */
export function isCalendarEventType(eventType: unknown): boolean {
    if (typeof eventType !== 'string') return false;
    return CALENDAR_PREFIXES.some((prefix) => eventType.startsWith(prefix));
}

// ── P1-1: статус календарного айтема (сессии/сессии) ───────────────────
//
// Единый source of truth для «прошло / идёт / отменено» на CalendarPage и
// CalendarHistoryPage. Раньше статус сессии считался инлайн-тернаром по
// ОДНОМУ start (`new Date(event.date) < new Date()`), BE-поля конца
// (end_time / duration_minutes из events/serializer.py) дропались — и идущий
// вебинар после перезагрузки страницы показывался «Завершено» в «Прошедших».

export type CalendarItemStatus = 'live' | 'done' | 'cancelled';

export interface CalendarItemTiming {
    /** ISO-старт (event.date / slot.start_time). */
    startIso: string | null | undefined;
    /** ISO-конец (event.end_time / slot.end_time), если BE его отдал. */
    endIso?: string | null;
    /** Длительность в минутах (BE duration_minutes) — фолбэк, когда endIso нет. */
    durationMinutes?: number | null;
}

/**
 * Момент, когда «окно подключения» айтема закрывается, ms epoch.
 *
 * Конец = endIso, иначе start + durationMinutes; к нему добавляется грейс
 * «можно досидеть» (sessionEndGraceMinutes — то же значение, что держит
 * Zoom-комнату живой на BE, CONFERENCE_PLANNED_END_GRACE_MIN). Если конца нет
 * вовсе — безопасный дефолт start + late-start-грейс (sessionGraceMin, зеркало
 * BE SESSION_GRACE_MIN), а не «закрыто ровно в момент старта».
 * NaN — когда времени нет вовсе (айтем никогда не считаем прошедшим).
 */
export function calendarItemJoinClosedMs(timing: CalendarItemTiming): number {
    const startMs = new Date(String(timing.startIso)).getTime();
    let endMs = timing.endIso ? new Date(timing.endIso).getTime() : NaN;
    if (!Number.isFinite(endMs) && Number.isFinite(startMs) && Number(timing.durationMinutes) > 0) {
        endMs = startMs + Number(timing.durationMinutes) * 60_000;
    }
    if (Number.isFinite(endMs)) return endMs + sessionEndGraceMinutes() * 60_000;
    if (Number.isFinite(startMs)) return startMs + sessionGraceMin() * 60_000;
    return NaN;
}

/** true, если окно подключения айтема уже закрылось («прошедшее»). */
export function isCalendarItemPast(
    timing: CalendarItemTiming,
    nowMs: number = Date.now(),
): boolean {
    const closedMs = calendarItemJoinClosedMs(timing);
    return Number.isFinite(closedMs) && closedMs < nowMs;
}

/**
 * Статус календарного айтема: cancelled → done/live по времени.
 * `nowMs` инжектится ради детерминированных тестов (как в canJoinEvent).
 */
export function deriveCalendarItemStatus(
    item: CalendarItemTiming & { isCanceled?: boolean | null },
    nowMs: number = Date.now(),
): CalendarItemStatus {
    if (item.isCanceled) return 'cancelled';
    return isCalendarItemPast(item, nowMs) ? 'done' : 'live';
}
